/**
 * LinkedInAutofillField — composite input that combines:
 *   - a regular text input for the LinkedIn URL
 *   - a "Pull from LinkedIn" button that calls /api/linkedin/extract
 *     and yields the structured fields back to the parent via `onAutofill`
 *
 * The parent is responsible for deciding which extracted fields to use.
 * For example, in a contact form the parent might destructure
 *   { name, headline, current_company, current_title }
 * and pre-fill its own form state.
 *
 * Props:
 *   value, onChange   — controlled text input
 *   onAutofill(fields, kind) — called when extraction returns ok=true
 *   variant           — 'profile' (default) or 'company'
 *   placeholder       — input placeholder text
 *   testId            — root data-testid
 */
import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import LinkedInButton from './LinkedInButton';

export default function LinkedInAutofillField({
  value, onChange, onAutofill,
  variant = 'profile',
  placeholder,
  testId = 'linkedin-autofill',
}) {
  const [loading, setLoading] = useState(false);

  const pull = async () => {
    if (!value || !value.trim()) {
      toast.error('Paste a LinkedIn URL first.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/linkedin/extract', { url: value.trim() });
      if (data.ok) {
        toast.success(data.message || 'LinkedIn fields imported.');
        onAutofill && onAutofill(data.fields || {}, data.kind);
        // Normalise the URL on success
        if (data.url) onChange(data.url);
      } else {
        // partial fields are still helpful — let the parent use whatever
        // came back (typically just og:title and og:description)
        if (data.fields && Object.keys(data.fields).length > 0) {
          onAutofill && onAutofill(data.fields, data.kind);
          toast.message(data.message || 'Partial data only.', {
            description: 'Please fill the rest manually.',
          });
        } else {
          toast.error(data.message || 'Could not extract — fill manually.');
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Extraction failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <div className="flex-1 relative">
        <input
          type="url"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || (variant === 'company'
            ? 'https://www.linkedin.com/company/...'
            : 'https://www.linkedin.com/in/...')}
          data-testid={`${testId}-input`}
          className="w-full text-sm rounded-md border border-slate-200 pl-10 pr-3 py-2 focus:outline-none focus:border-[#8B3FC5]"
        />
        <div className="absolute left-3 top-1/2 -translate-y-1/2">
          <LinkedInButton url={value} size={14}/>
        </div>
      </div>
      <button
        type="button"
        onClick={pull}
        disabled={loading || !value?.trim()}
        data-testid={`${testId}-pull`}
        className="ve-btn-secondary text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-2 whitespace-nowrap disabled:opacity-50 inline-flex items-center gap-1"
        title="Use AI to extract name, title, company from this LinkedIn URL"
      >
        {loading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
        {loading ? 'Pulling…' : 'Pull from LinkedIn'}
      </button>
    </div>
  );
}
